import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { EXTERNAL_PLAY_CONTEXT } from 'video-embed/constants';
import { PlayerTypes } from 'video-embed/types';
import { isAuthedAppPreview } from 'video-embed/utils/env';
import { debugLog } from 'video-embed/utils/debug';
import { getVideo, getVideoFetchError } from '../selectors';
import { getSessionId } from '../utils/tracker';
import { setupRetentionTracking } from '../utils/retention';
import { cleanPageUrl } from '../utils/url';
import PlayerContainer from './PlayerContainer';
import { Metrics } from '../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
const ALLOW_TRACKING_WITHOUT_ADVANCED_FEATURES_PORTAL_IDS = [53];
class EmbedContainer extends PureComponent {
  constructor(...args) {
    super(...args);
    this.trackedInitialPlay = false;
    this.trackInitialPlayUsage = () => {
      if (!this.trackedInitialPlay) {
        this.props.trackUsage('playerInteraction', {
          action: 'play'
        });
        this.trackedInitialPlay = true;
      }
    };
  }
  async componentDidMount() {
    if (this.isPublicEmbed()) {
      this.initRetentionTracking();
    }
    if (this.props.utk) {
      await this.handleSetUtk();
    }
    if (this.props.pageMeta) {
      await this.handleSetPageMeta();
    }
  }
  async componentDidUpdate(prevProps) {
    const {
      utk,
      pageMeta
    } = this.props;
    if (utk !== prevProps.utk && !isAuthedAppPreview()) {
      await this.handleSetUtk();
    }
    if (pageMeta && pageMeta !== prevProps.pageMeta) {
      await this.handleSetPageMeta();
    }
  }
  isValidEmbed() {
    const {
      params,
      debugFlagEnabled,
      pageMeta
    } = this.props;
    return isAuthedAppPreview() || params.playerType === PlayerTypes.RICH_TEXT_PREVIEW || params.playerType === PlayerTypes.EDITOR_PREVIEW || Boolean(pageMeta && pageMeta.pageUrl) || debugFlagEnabled;
  }
  isPublicEmbed() {
    const {
      params
    } = this.props;
    return !isAuthedAppPreview() && params.playerType !== PlayerTypes.RICH_TEXT_PREVIEW && params.playerType !== PlayerTypes.EDITOR_PREVIEW;
  }
  async handleSetPageMeta() {
    const {
      pageMeta
    } = this.props;
    const pageUrl = cleanPageUrl(pageMeta.pageUrl);
    this.props.tracker.setProperties({
      pageUrl
    });
    if (this.reporter) {
      this.reporter.pageMeta = pageMeta;
      if (this.videoJsPlayer && this.videoJsPlayer.hasStarted()) {
        await this.reporter.trackPlay();
      }
    }
    if (this.isValidEmbed()) {
      debugLog(`Validated CMS embed for player: ${this.props.embedId}`);
    } else {
      console.error('Received invalid payload from SET_PAGE_META, blocking play', {
        extra: pageMeta
      });
      Metrics.counter('page-meta-payload-invalid').increment();
    }
  }
  async handleSetUtk() {
    const {
      utk
    } = this.props;
    if (this.reporter) {
      // ensure we track initial play if it occurred before utk arrived,
      // likely when autoplay is used or cookie banner enabled (`trackPlay` ensures we only record one play event when call multiple times)
      this.reporter.utk = utk;
      if (this.videoJsPlayer && this.videoJsPlayer.hasStarted()) {
        await this.reporter.trackPlay();
      }
    }
  }
  initRetentionTracking() {
    const {
      playerType,
      video,
      utk,
      pageMeta,
      postMessageToParent
    } = this.props;
    const embeddableTypes = [PlayerTypes.RICH_TEXT_PREVIEW, PlayerTypes.EDITOR_PREVIEW, PlayerTypes.HTML5];
    if (this.reporter || !video || embeddableTypes.includes(playerType)) {
      return;
    }
    if (!video.isEmbeddable && !ALLOW_TRACKING_WITHOUT_ADVANCED_FEATURES_PORTAL_IDS.includes(video.portalId) && (pageMeta === null || pageMeta === void 0 ? void 0 : pageMeta.externalPlayContext) !== EXTERNAL_PLAY_CONTEXT.EMAIL) {
      return;
    }
    if (video && this.videoJsPlayer) {
      this.reporter = setupRetentionTracking(video, this.videoJsPlayer, {
        embedId: this.props.embedId,
        sessionId: getSessionId(),
        utk,
        pageMeta,
        postMessageToParent
      }, this.props.trackUsage);
    }
  }
  render() {
    const {
      embedId,
      params,
      utk,
      pageMeta,
      postMessageToParent
    } = this.props;
    return /*#__PURE__*/_jsx(PlayerContainer, {
      embedId: embedId,
      params: params,
      playerType: this.props.playerType,
      video: this.props.video,
      videoFetchError: this.props.videoFetchError,
      utk: utk,
      pageMeta: pageMeta,
      embedValidated: true,
      currentTime: this.props.currentTime,
      postMessageToParent: postMessageToParent,
      trackUsage: this.props.trackUsage,
      trackInitialPlayUsage: this.trackInitialPlayUsage,
      onCreatePlayer: (playerContainer, videoJsPlayer) => {
        // to allow console debugging via `window._app.embedContainer.playerContainer
        this.playerContainer = playerContainer;
        this.videoJsPlayer = videoJsPlayer;
        if (this.props.video) {
          document.body.classList.add('player-mounted');
        }
        if (this.props.playerType) {
          document.body.setAttribute('data-player-type', this.props.playerType);
        }
        this.props.onCreatePlayer(playerContainer, videoJsPlayer);
      }
    });
  }
}
const mapStateToProps = state => ({
  video: getVideo(state),
  videoFetchError: getVideoFetchError(state)
});
export default connect(mapStateToProps)(EmbedContainer);